'use client'

import { BookOpen, ChevronDown, Search, X } from 'lucide-react'
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { locationMapHeroTriggerClassName } from '@/components/Location/LocationCampusDropdown'
import { matchesUniversityQuery } from '@/lib/locations/universityDataset'
import type { LocationUniversityOption } from '@/lib/mock/locationPages'
import { cn } from '@/utilities/ui'

type LocationUniversitySearchProps = {
  accentSelected?: boolean
  clearLabel: string
  emptyLabel: string
  fullWidth?: boolean
  label: string
  listAriaLabel: string
  onClear?: () => void
  onSelect: (university: LocationUniversityOption) => void
  placeholder: string
  selectedLabelMaxLength?: number
  universities: LocationUniversityOption[]
  uppercase?: boolean
  value: LocationUniversityOption | null
  wrapMenuOptions?: boolean
}

function truncateSelectedLabel(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text
  }

  const slice = text.slice(0, maxLength).trimEnd()
  const lastSpace = slice.lastIndexOf(' ')

  if (lastSpace > maxLength * 0.55) {
    return `${slice.slice(0, lastSpace)}…`
  }

  return `${slice}…`
}

const TAP_MOVE_THRESHOLD_PX = 10

function getUniversityOptionButtons(root: HTMLElement): HTMLButtonElement[] {
  return [...root.querySelectorAll<HTMLButtonElement>('[data-university-option]')]
}

type PanelPosition = {
  left: number
  top: number
  width: number
}

function getPanelPosition(trigger: HTMLElement, fullWidth: boolean): PanelPosition {
  const rect = trigger.getBoundingClientRect()
  const viewportPadding = 16
  const desktopPanelWidth = 26 * 16
  const mobileMaxWidth = Math.min(window.innerWidth - viewportPadding * 2, desktopPanelWidth)
  const width = fullWidth
    ? rect.width
    : Math.max(rect.width, mobileMaxWidth)

  return {
    left: Math.min(rect.left, window.innerWidth - width - viewportPadding),
    top: rect.bottom + 8,
    width,
  }
}

export function LocationUniversitySearch({
  accentSelected = false,
  clearLabel,
  emptyLabel,
  fullWidth = false,
  label,
  listAriaLabel,
  onClear,
  onSelect,
  placeholder,
  selectedLabelMaxLength,
  universities,
  uppercase = false,
  value,
  wrapMenuOptions = false,
}: LocationUniversitySearchProps) {
  const listId = useId()
  const searchId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const triggerButtonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const blurCloseTimeoutRef = useRef<number | null>(null)
  const optionTouchRef = useRef<{ slug: string; x: number; y: number } | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null)

  const openPanel = () => {
    setOpen(true)
    setQuery('')
  }

  const closePanel = (returnFocus = false) => {
    setOpen(false)
    setQuery('')

    if (returnFocus) {
      triggerButtonRef.current?.focus()
    }
  }

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPanelPosition(null)
      return
    }

    const updatePosition = () => {
      if (!triggerRef.current) {
        return
      }

      setPanelPosition(getPanelPosition(triggerRef.current, fullWidth))
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [fullWidth, open])

  const filteredUniversities = useMemo(
    () => universities.filter((university) => matchesUniversityQuery(university, query)),
    [query, universities],
  )

  useEffect(() => {
    if (!open) {
      return
    }

    searchRef.current?.focus()

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node

      if (rootRef.current?.contains(target)) {
        return
      }

      setOpen(false)
      setQuery('')
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePanel(true)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  useEffect(() => {
    return () => {
      if (blurCloseTimeoutRef.current != null) {
        window.clearTimeout(blurCloseTimeoutRef.current)
      }
    }
  }, [])

  const rawTriggerLabel = value?.name ?? label
  const compactTriggerLabel =
    value && selectedLabelMaxLength != null
      ? truncateSelectedLabel(rawTriggerLabel, selectedLabelMaxLength)
      : rawTriggerLabel
  const triggerLabel = uppercase ? compactTriggerLabel.toUpperCase() : compactTriggerLabel
  const accessibleLabel = value ? `${label}: ${value.name}` : label
  const useAccent = accentSelected && Boolean(value)
  const canClear = Boolean(value && onClear)

  const focusOptionAt = (index: number) => {
    const options = panelRef.current ? getUniversityOptionButtons(panelRef.current) : []
    options[index]?.focus()
  }

  const handleRootBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (blurCloseTimeoutRef.current != null) {
      window.clearTimeout(blurCloseTimeoutRef.current)
    }

    // Touch taps blur the search field with a null relatedTarget before the option receives focus.
    blurCloseTimeoutRef.current = window.setTimeout(() => {
      blurCloseTimeoutRef.current = null
      const nextTarget = event.relatedTarget

      if (nextTarget instanceof Node && rootRef.current?.contains(nextTarget)) {
        return
      }

      const activeElement = document.activeElement
      if (activeElement instanceof Node && rootRef.current?.contains(activeElement)) {
        return
      }

      setOpen(false)
      setQuery('')
    }, 0)
  }

  const handleOptionSelect = (university: LocationUniversityOption) => {
    onSelect(university)
    closePanel(true)
  }

  const handleOptionPointerDown = (
    event: React.PointerEvent<HTMLButtonElement>,
    university: LocationUniversityOption,
  ) => {
    if (event.pointerType === 'mouse') {
      // Prevent the search field from blurring before click on Safari/desktop.
      event.preventDefault()
      return
    }

    if (event.pointerType !== 'touch') {
      return
    }

    optionTouchRef.current = {
      slug: university.slug,
      x: event.clientX,
      y: event.clientY,
    }
  }

  const handleOptionPointerUp = (
    event: React.PointerEvent<HTMLButtonElement>,
    university: LocationUniversityOption,
  ) => {
    if (event.pointerType !== 'touch' || !optionTouchRef.current) {
      return
    }

    if (optionTouchRef.current.slug !== university.slug) {
      optionTouchRef.current = null
      return
    }

    const deltaX = Math.abs(event.clientX - optionTouchRef.current.x)
    const deltaY = Math.abs(event.clientY - optionTouchRef.current.y)
    optionTouchRef.current = null

    if (deltaX > TAP_MOVE_THRESHOLD_PX || deltaY > TAP_MOVE_THRESHOLD_PX) {
      return
    }

    // Select on tap before blur unmounts the option list.
    event.preventDefault()
    handleOptionSelect(university)
  }

  const handleOptionPointerCancel = () => {
    optionTouchRef.current = null
  }

  const handleListScroll = () => {
    optionTouchRef.current = null
  }

  const handleClear = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault()
    event.stopPropagation()
    closePanel(true)
    onClear?.()
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusOptionAt(0)
      return
    }

    if (event.key === 'Tab' && !event.shiftKey) {
      if (filteredUniversities.length === 0) {
        setOpen(false)
        setQuery('')
        return
      }

      event.preventDefault()
      focusOptionAt(0)
    }

    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault()
      closePanel(true)
    }
  }

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      const nextIndex = Math.min(filteredUniversities.length - 1, index + 1)
      const options = panelRef.current ? getUniversityOptionButtons(panelRef.current) : []
      focusOptionAt(nextIndex)
      options[nextIndex]?.scrollIntoView({ block: 'nearest' })
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()

      if (index === 0) {
        searchRef.current?.focus()
        return
      }

      const previousIndex = index - 1
      const options = panelRef.current ? getUniversityOptionButtons(panelRef.current) : []
      focusOptionAt(previousIndex)
      options[previousIndex]?.scrollIntoView({ block: 'nearest' })
      return
    }

    if (event.key === 'Tab' && !event.shiftKey) {
      if (index < filteredUniversities.length - 1) {
        event.preventDefault()
        focusOptionAt(index + 1)
      }

      return
    }

    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault()

      if (index === 0) {
        searchRef.current?.focus()
        return
      }

      focusOptionAt(index - 1)
    }
  }

  return (
    <div
      className={cn('relative min-w-0', fullWidth && 'min-w-0 flex-1', !fullWidth && 'self-start')}
      onBlur={handleRootBlur}
      ref={rootRef}
    >
      <div
        ref={triggerRef}
        className={cn(
          locationMapHeroTriggerClassName,
          'shadow-none ring-0 transition-colors hover:bg-cream focus-within:outline-none focus-within:ring-2 focus-within:ring-turquoise/50 focus-within:ring-offset-2 focus-within:ring-offset-cream',
          fullWidth ? 'flex w-full min-w-0 max-w-full' : 'inline-flex max-w-full flex-nowrap',
          useAccent && 'text-dark-green',
        )}
      >
        <button
          aria-controls={listId}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={accessibleLabel}
          className={cn(
            'inline-flex min-w-0 flex-1 items-center gap-2 bg-transparent text-left',
            useAccent ? 'text-dark-green' : 'text-dark-800/90',
          )}
          onClick={() => {
            if (open) {
              closePanel(true)
              return
            }

            openPanel()
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openPanel()
            }
          }}
          ref={triggerButtonRef}
          title={value?.name}
          type="button"
        >
          <BookOpen aria-hidden className="size-4 shrink-0 text-dark-green" />
          <span
            className={cn(
              'min-w-0 font-outfit text-sm font-semibold tracking-wide',
              (value && selectedLabelMaxLength != null) || fullWidth ? 'truncate' : undefined,
              value && !fullWidth && selectedLabelMaxLength != null && 'max-w-[11rem]',
            )}
          >
            {triggerLabel}
          </span>
        </button>

        {canClear ? (
          <button
            aria-label={clearLabel}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-dark-800/70 transition-colors hover:bg-cream hover:text-dark-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50"
            onClick={handleClear}
            type="button"
          >
            <X aria-hidden className="size-3.5" />
          </button>
        ) : (
          <button
            aria-expanded={open}
            aria-label={accessibleLabel}
            className={cn(
              'inline-flex size-7 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50',
              useAccent ? 'text-dark-green' : 'text-dark-800/70',
            )}
            onClick={() => {
              if (open) {
                closePanel(true)
                return
              }

              openPanel()
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                openPanel()
              }
            }}
            type="button"
          >
            <ChevronDown aria-hidden className="size-4 opacity-60" />
          </button>
        )}
      </div>

      {open && panelPosition ? (
        <div
          className={cn(
            'nw-map-overlay-layer fixed flex max-h-[min(24rem,calc(100dvh-2rem))] flex-col overflow-hidden rounded-[10px] border border-black/10 bg-white shadow-[0px_16px_32px_-8px_rgba(0,33,64,0.12)]',
            fullWidth ? 'min-w-[16rem]' : 'md:max-w-[26rem]',
          )}
          data-lenis-prevent
          id={listId}
          onTouchMove={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
          ref={panelRef}
          style={{
            left: panelPosition.left,
            top: panelPosition.top,
            width: panelPosition.width,
          }}
        >
          <div className="shrink-0 border-b border-black/10 p-2">
            <label className="sr-only" htmlFor={searchId}>
              {placeholder}
            </label>
            <div className="flex items-center gap-2 rounded-[8px] border border-black/10 bg-cream px-3 py-2">
              <Search aria-hidden className="size-4 shrink-0 text-dark-green" />
              <input
                autoComplete="off"
                className="min-w-0 flex-1 bg-transparent font-outfit text-base text-dark-800 outline-none placeholder:text-dark-800/50 md:text-sm"
                id={searchId}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={placeholder}
                ref={searchRef}
                role="combobox"
                aria-autocomplete="list"
                aria-controls={listId}
                aria-expanded={open}
                type="search"
                value={query}
              />
            </div>
          </div>

          {filteredUniversities.length === 0 ? (
            <p className="px-4 py-3 font-outfit text-sm text-dark-800/70" role="status">
              {emptyLabel}
            </p>
          ) : (
            <ul
              aria-label={listAriaLabel}
              className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain py-1 [-webkit-overflow-scrolling:touch]"
              onScroll={handleListScroll}
              onTouchMove={(event) => event.stopPropagation()}
              onWheel={(event) => event.stopPropagation()}
              role="listbox"
            >
              {filteredUniversities.map((university, index) => {
                const selected = university.slug === value?.slug

                return (
                  <li key={university.slug} role="none">
                    <button
                      aria-selected={selected}
                      className={cn(
                        'flex w-full px-4 py-2.5 text-left font-outfit text-sm font-medium text-dark-800 hover:bg-cream focus-visible:bg-cream focus-visible:outline-none',
                        selected && 'bg-cream text-bluetext',
                        wrapMenuOptions && 'whitespace-normal break-words leading-snug',
                      )}
                      data-university-option
                      onClick={() => handleOptionSelect(university)}
                      onKeyDown={(event) => handleOptionKeyDown(event, index)}
                      onPointerCancel={handleOptionPointerCancel}
                      onPointerDown={(event) => handleOptionPointerDown(event, university)}
                      onPointerUp={(event) => handleOptionPointerUp(event, university)}
                      role="option"
                      type="button"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span>{university.name}</span>
                        {university.district ? (
                          <span className="font-outfit text-xs font-medium text-dark-800/65">
                            {university.district}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
